import axios from 'Src/helpers/api/axiosInstance';
import { AppUser } from 'Src/views/App/App';
import { SchoolStudent } from '../../../models/schoolStudent';
import { Count } from '../../../models/count';

const FORMS_IN_CHUNK = 200;
const IDS_NIN_CHUNK = 500;
const PAGE_LIMIT = 500;

type AnyObj = Record<string, any>;

function chunk<T>(arr: T[], size: number): T[][] {
  if (!Array.isArray(arr) || !arr.length) return [[]];
  const out: T[][] = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
}

async function postStudentsListBatch(user: AppUser, body: AnyObj): Promise<SchoolStudent[]> {
  const { roleSessionKey: key, activeSchoolId } = user;

  const url = `${window.apiBase}/i/schools/${activeSchoolId}/studentsList`;
  const data = {
    ...body,
    limit: Math.min(Number(body?.limit ?? PAGE_LIMIT), PAGE_LIMIT),
    skip: Number(body?.skip ?? 0)
  };
  const config = { headers: { usid: key } };

  const { data: res } = await axios.post<SchoolStudent[]>(url, data, config);
  return res;
}

async function getStudentsListBatch(user: AppUser, body: AnyObj): Promise<SchoolStudent[]> {
  const requestedLimit = Math.max(0, Number(body?.limit ?? PAGE_LIMIT));
  const requestedSkip = Math.max(0, Number(body?.skip ?? 0));

  if (requestedLimit <= PAGE_LIMIT) {
    return postStudentsListBatch(user, body);
  }

  const students: SchoolStudent[] = [];

  while (students.length < requestedLimit) {
    const limit = Math.min(PAGE_LIMIT, requestedLimit - students.length);
    const page = await postStudentsListBatch(user, {
      ...body,
      limit,
      skip: requestedSkip + students.length
    });

    students.push(...page);

    if (page.length < limit) break;
  }

  return students;
}

export async function getSchoolStudentsList(user: AppUser, filter: AnyObj): Promise<SchoolStudent[]> {
  const where = filter?.where ?? {};

  const formsIn: string[] = Array.isArray(where?.formId?.$in) ? where.formId.$in : [];
  const idsNin: string[] = Array.isArray(where?._id?.$nin) ? where._id.$nin : [];

  const needChunkForms = formsIn.length > FORMS_IN_CHUNK;
  const needChunkNin = idsNin.length > IDS_NIN_CHUNK;

  if (!needChunkForms && !needChunkNin) {
    return getStudentsListBatch(user, filter);
  }

  const formChunks = needChunkForms ? chunk(formsIn, FORMS_IN_CHUNK) : [formsIn];
  const ninChunks = needChunkNin ? chunk(idsNin, IDS_NIN_CHUNK) : [idsNin];

  const results: SchoolStudent[] = [];

  for (const fc of formChunks) {
    for (const nc of ninChunks) {
      const body: AnyObj = {
        ...filter,
        where: {
          ...where,
          ...(fc.length ? { formId: { $in: fc } } : {}),
          ...(nc.length ? { _id: { $nin: nc } } : {})
        },
        limit: Number(filter?.limit ?? PAGE_LIMIT),
        skip: 0
      };

      const page = await getStudentsListBatch(user, body);
      results.push(...page);
    }
  }

  const seen = new Set<string>();
  return results.filter(s => {
    const id = String((s as any)?._id ?? (s as any)?.id ?? '');
    if (!id || seen.has(id)) return false;
    seen.add(id);
    return true;
  });
}

export async function getSchoolStudentsListCount(user: AppUser, filter: AnyObj): Promise<Count> {
  const { roleSessionKey: key, activeSchoolId } = user;
  const config = { headers: { usid: key } };
  const body = { ...filter, skip: 0, limit: undefined };

  const { data } = await axios.post<Count>(
    `${window.apiBase}/i/schools/${activeSchoolId}/studentsList/count`,
    body,
    config
  );
  return data;
}

export async function searchSchoolStudents(
  user: AppUser,
  filter: AnyObj
): Promise<{ items: SchoolStudent[]; total: number }> {
  const { roleSessionKey: key, activeSchoolId } = user;
  const config = { headers: { usid: key } };

  const { data } = await axios.post(
    `${window.apiBase}/i/schools/${activeSchoolId}/studentsList/search`,
    filter,
    config
  );

  return {
    items: Array.isArray(data.items) ? data.items : [],
    total: Number(data.total || 0)
  };
}
